import { useContext, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import isEqual from 'lodash/isEqual';

import { useBooleanState } from '@vyce/core/src/hooks';
import { PaySchedule, Site } from '@vyce/core/src/types';
import { TeamsSyncSettingsDTO } from '@vyce/core/src/api/types';

import { defaultValues } from '../config';
import { getTransformedData } from '../utils';
import { EnrichedTeamsSyncSettingsDTO, Props } from '../types';
import { NotificationContext } from '../../../contexts/notificationContext';

export const useTeamSettingsData = ({
  teamId,
  selectedCompanyId,
  getTeamsSyncSettingsRequest,
  updateTeamsSyncSettingsRequest,
  getPaySchedulesRequest,
  getSitesRequest,
}: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [mainLoading, setMainLoadingTrue, setMainLoadingFalse] = useBooleanState(true);
  const [buttonLoading, setButtonLoadingTrue, setButtonLoadingFalse] = useBooleanState(false);
  const [settings, setSettings] = useState(defaultValues);
  const [isShowButton, setIsShowButton] = useState(false);
  const [paySchedules, setPaySchedules] = useState<PaySchedule[]>([]);
  const [sites, setSites] = useState<Site[]>([]);

  const methods = useForm<EnrichedTeamsSyncSettingsDTO>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: settings,
  });

  const { watch, getValues, reset } = methods;
  const activate = watch('active');
  const isTimeSync = watch('isTimeSync');
  const isPaySync = watch('isPaySync');
  const payPeriodsCount = watch('recent_pay_periods');
  const lastCheckinWeeksCount = watch('recent_time_periods');
  const selectedPaySchedules = watch('pay_schedules');
  const selectedTimeSites = watch('time_sites');

  const fetchTeamsSyncSettings = async () => {
    if (!selectedCompanyId || !teamId) {
      setMainLoadingFalse();
      return;
    }
    setMainLoadingTrue();
    try {
      const [{ data }, paySchedulesRes, sitesRes] = await Promise.all([
        getTeamsSyncSettingsRequest({ companyId: selectedCompanyId, teamId }),
        getPaySchedulesRequest({ companyId: selectedCompanyId, limit: 1000 }),
        getSitesRequest(selectedCompanyId, {
          limit: 1000,
          only_active: true,
          show_deleted: false,
          order_by: [],
        }),
      ]);
      const fetchedPaySchedules = paySchedulesRes.data.items ?? [];
      const fetchedSites = sitesRes.data.items ?? [];
      setPaySchedules(fetchedPaySchedules);
      setSites(fetchedSites);
      setSettings(getTransformedData(data, fetchedPaySchedules, fetchedSites));
      setMainLoadingFalse();
    } catch (e) {
      console.error(e);
      handleServerError(e);
      setMainLoadingFalse();
    }
  };

  const updateTeamsSettings = async (data: EnrichedTeamsSyncSettingsDTO) => {
    if (!selectedCompanyId || !teamId) {
      return;
    }

    setButtonLoadingTrue();

    const payload: TeamsSyncSettingsDTO = {
      active: data.active,
      recent_pay_periods: data.isPaySync ? data.recent_pay_periods : 0,
      recent_time_periods: data.isTimeSync ? data.recent_time_periods : 0,
      pay_schedules: data.isPaySync ? data.pay_schedules.map(schedule => schedule.uuid ?? '') : [],
      time_sites: data.isTimeSync ? data.time_sites.map(site => site.uuid ?? '') : [],
    };
    try {
      const { data: responseData } = await updateTeamsSyncSettingsRequest({
        companyId: selectedCompanyId,
        teamId,
        payload,
      });
      setSettings(getTransformedData(responseData, paySchedules, sites));
      setButtonLoadingFalse();
      showNotification({ message: `Settings was saved`, options: { variant: 'success' } });
    } catch (e) {
      console.error(e);
      handleServerError(e);
      setButtonLoadingFalse();
    }
  };

  useEffect(() => reset(settings), [reset, settings]);

  useEffect(() => {
    setIsShowButton(!isEqual(getValues(), settings) || buttonLoading);
  }, [
    payPeriodsCount,
    lastCheckinWeeksCount,
    activate,
    isTimeSync,
    isPaySync,
    selectedPaySchedules,
    selectedTimeSites,
    settings,
    buttonLoading,
  ]);

  useEffect(() => {
    fetchTeamsSyncSettings();
  }, [selectedCompanyId, teamId]);

  return {
    mainLoading,
    buttonLoading,
    settings,
    methods,
    activate,
    isTimeSync,
    isPaySync,
    isShowButton,
    paySchedules,
    sites,
    updateTeamsSettings,
  };
};
